import type { OrderItem } from '@/types/api';

/**
 * Line-item quantity. The backend doesn't send `quantity` yet — missing
 * means a single unit.
 */
export function itemQty(item: OrderItem): number {
  return item.quantity ?? 1;
}

/**
 * Groups flat order items into drinks with their modifiers nested — cart
 * checkouts insert all drink rows before any modifier rows, so the API order
 * alone can't be used for grouping; `parentItemId` is the source of truth.
 * Legacy (pre-cart) orders never set `parentItemId` since there was only
 * ever one drink, so a null parent falls back to the sole drink — same rule
 * the customer app uses.
 */
export function groupOrderItems(items: OrderItem[]): { drink: OrderItem; modifiers: OrderItem[] }[] {
  const drinks = items.filter((i) => i.type === 'drink');
  if (drinks.length === 0) {
    return items.map((i) => ({ drink: i, modifiers: [] }));
  }

  const others = items.filter((i) => i.type !== 'drink');
  const soleDrinkId = drinks.length === 1 ? drinks[0].id : null;
  const matched = new Set<number>();

  const grouped = drinks.map((drink) => ({
    drink,
    modifiers: others.filter((m) => {
      const belongsToDrink =
        m.parentItemId === drink.id || (m.parentItemId == null && soleDrinkId === drink.id);
      if (belongsToDrink) matched.add(m.id);
      return belongsToDrink;
    }),
  }));

  // Orphaned modifiers (parentItemId set but unmatched among 2+ drinks) are
  // kept as top-level rows rather than dropped, so a data anomaly can't hide
  // a paid line from the receipt.
  const orphans = others.filter((m) => !matched.has(m.id)).map((m) => ({ drink: m, modifiers: [] }));

  return [...grouped, ...orphans];
}

/** The drink-only lines of an order — used for compact card summaries. */
export function drinkItems(items: OrderItem[]): OrderItem[] {
  return items.filter((i) => i.type === 'drink');
}

/**
 * Compact one-line summary of an order's drinks for list rows, e.g.
 * "Latte ×2, Green Tea". Falls back to `fallback` (the legacy single-drink
 * name) when the order carries no line items.
 */
export function drinkSummary(items: OrderItem[], fallback: string): string {
  const drinks = drinkItems(items);
  if (drinks.length === 0) return fallback;
  return drinks
    .map((d) => (itemQty(d) > 1 ? `${d.name} ×${itemQty(d)}` : d.name))
    .join(', ');
}
